import ValidateModule from "./ValidateModule";

import type { IEventChannel, ApiOpts } from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type CacheModule from "./CacheModule";

const $: JQueryStatic = global.$;
const es = global.es;

/**
 * @class ImportModule
 */
export default class ImportModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;

    IMPORT_ACTION_TYPE = {
        "NEW_IMPORT" : "NEW_IMPORT",
        "UPSERT" : "UPSERT",
        "APPEND" : "APPEND",
        "OVERWRITE" : "OVERWRITE",
        "MERGE_OVERWRITE_ESPATIAL_CHANGES" : "MERGE_OVERWRITE_ESPATIAL_CHANGES"
    }

    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        // TODO FIXME Does this even make sense nowadays?
        eventChannel.addListener("onDestroy", () => {
            // prevent memory leaks when the map widget is destroyed
            this.eventChannel = this.rqBroker = this.resourceName = this.cache = null;
        });
        
        this.importURL = this.importURL.bind(this);
        this.poll = this.poll.bind(this);
        this.wholeImport = this.wholeImport.bind(this);
        this.wholeImportNoPoll = this.wholeImportNoPoll.bind(this);
        this.beginImportURL = this.beginImportURL.bind(this);
        this.importCreate = this.importCreate.bind(this);
        this.importEdit = this.importEdit.bind(this);
        this.getSampleFile = this.getSampleFile.bind(this);
        this.getProgress = this.getProgress.bind(this);
        this.stopProgress = this.stopProgress.bind(this);
        this.progressDone = this.progressDone.bind(this);
        this.getValidAttributeName = this.getValidAttributeName.bind(this);
    }


    // TODO: get rid of the number and just use an enum
    /*
    IMPORT_ACTION_TYPE_from_num(num: number) {
        if(num == 0){
            return "NEW_IMPORT";
        } else if(num == 1){
            return "APPEND";
        } else if (num == 2){
            return "OVERWRITE";
        }
    }
    */
    
    /**
     * Import file by specifying: the url of a csv, xls or xlsx file (e.g. https://dl.dropboxusercontent.com/u/417235/castles_2.csv)
     *
     * @param url url of resource
     * @param layerMetaId
     * @param updateType
     * @param selector String. Only required if ImportDataType is either:
     *           a) HTML - selector is xpath to the table element in the html page to be imported e.g. "table", "table[class=Trolley]"
     *           b) XML - selector is the name of the xml element which marks a row. e.g. in the following xml, the row selector would be "row" <xml><row><a>a</a><b>b</b></row><row><a>a</a><b>b</b></row><row><a>a</a><b>b</b></row></xml>
     * @param defaultPresetAddress country bias for use if/when geocoding (default "United States")
     * @param isAllOrNothing
     * @param opts
     *
     * @return a jquery deferred object resolved when the request has completed
     */
    importURL(url: string, layerMetaId: number, updateType: string, selector?: string, defaultPresetAddress?: string, isAllOrNothing?: boolean, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(url) ||
            !ValidateModule.checkId(layerMetaId) ||
            !ValidateModule.checkMandatoryArg(updateType)) {
            return es.util.deferred.rejected();
        }
        
        url = es.api.getURL(this.resourceName) + "/url?" +
            "url=" + url +
            "&layerMetaId=" + layerMetaId +
            "&isAllOrNothing=" + (isAllOrNothing ? true : false) +
            "&updateType=" + updateType +
            ( selector ? "&selector=" + selector : "") +
            ( defaultPresetAddress ? "&defaultPresetAddress=" + es.util.url.stringify(defaultPresetAddress) : "");

        return this.rqBroker.doPost({
            url: url,
            opts: opts,
            data: {}
        });
    }
    
    /**
     * 
     * @param taskId 
     * @param opts 
     * 
     * @returns 
     */
    poll(taskId: number, opts?: ApiOpts) {
        const dfd = $.Deferred();
        
        // check progress of file
        const checkProgress = () => {
            this.getProgress(taskId).done((rs) => {
                if (!rs || !rs["statusObj"]) {
                    // Task no longer exists (completed and cleaned up, or invalid taskId)
                    dfd.resolve(rs);
                } else if (rs.statusObj.dataLoadProcessing === false) {
                    dfd.resolve(rs);

                    this.cache.clear("Security", "ownership_layermeta");
                    this.cache.clear("Security", "ownership_layerview");
                    this.cache.clear("Thematic_GET_COLOR");
                    this.cache.clear("Thematic_GET_SYMBOL");
                    // no need to this.cache.clear("Thematic_GET_SIZE") as we only recalculate distinct thematics and size is always range thematic
                } else {
                    window.setTimeout(() => checkProgress(), 1000); // check progress again in a second
                }
            }).fail((rs) => {
                dfd.reject("Error polling import", rs);
            });
        };
        checkProgress();

        return dfd.promise();
    }

    /**
     * Import a URL and create a dataset from it.
     * 
     * TODO: combine with scheduledUpdate?
     * 
     * @param url 
     * @param geoMapId 
     * @param importDataType 
     * @param selector 
     * @param defaultPresetAddress 
     * @param isAllOrNothing 
     * @param opts 
     * 
     * @returns 
     */
    wholeImport(url: string, geoMapId: number, importDataType?: string, selector?: string, defaultPresetAddress?: string, isAllOrNothing?: boolean, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(url) ||
            !ValidateModule.checkId(geoMapId)) {
            return es.util.deferred.rejected();
        }

        // geocodeCols
        url = es.api.getURL(this.resourceName) + "/whole_import?" +
            "url=" + url +
            ( importDataType ? "&importDataType=" + importDataType : "") +
            ( selector ? "&selector=" + selector : "") +
            "&isAllOrNothing=" + (isAllOrNothing ? true : false) +
            "&geoMapId=" + geoMapId +
            ( defaultPresetAddress ? "&defaultPresetAddress=" + es.util.url.stringify(defaultPresetAddress) : "");

        return this.rqBroker.doPost({
            url: url,
            opts: opts,
            data: {}
        });
    }
    
    /**
     * Call wholeImport but adds a wrapper around the poll part and resolves when the import has completed.
     * 
     * @param url 
     * @param geoMapId 
     * @param importDataType 
     * @param selector 
     * @param defaultPresetAddress 
     * @param isAllOrNothing 
     * @param opts 
     * 
     * @returns 
     */
    wholeImportNoPoll(url: string, geoMapId: number, importDataType?: string, selector?: string, defaultPresetAddress?: string, isAllOrNothing?: boolean, opts?: ApiOpts) {
        return this.wholeImport(url, geoMapId, importDataType, selector, defaultPresetAddress, isAllOrNothing, opts).then((rs) => {
            return this.poll(rs.taskId);
        });
    }
    
    /**
     * Begin import by url
     * 
     * @param url 
     * @param selector 
     * @param opts 
     * 
     * @returns 
     */
    beginImportURL(url: string, selector: string, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(url) ) {
            return es.util.deferred.rejected();
        }
        
        url = es.api.getURL(this.resourceName) + "/url_begin?" +
            "url=" + url +
            ( selector ? "&selector=" + selector : "");

        return this.rqBroker.doPost({
            url: url,
            opts: opts,
            data: {}
        });
    }

    /**
     * Create feature by passing key-val pairs for attributes and a layerMetaId.
     * Feature will be geocoded on the server using same geomExtractor as the original dataset.
     * 
     * @param attrs feature attributes as key-value pairs e.g. {"Lat": 52.312444, "Lon": -7.271458, "Castle Name": "Arderra Castle", "Clan Name": "Walsh"}
     * @param layerMetaId
     * @param importDataType (CSV, XML, JSON, HTML) defaults to JSON
     * @param selector String. Only required if ImportDataType is either:
     *           a) HTML - selector is xpath to the table element in the html page to be imported e.g. "table", "table[class=Trolley]"
     *           b) XML - selector is the name of the xml element which marks a row. e.g. in the following xml, the row selector would be "row" <xml><row><a>a</a><b>b</b></row><row><a>a</a><b>b</b></row><row><a>a</a><b>b</b></row></xml>
     * @param defaultPresetAddress used for geocoding, defaults to United States
     * @param opts
     * 
     * @returns
     */
    importCreate(attrs, layerMetaId: number, importDataType: string, selector: string, defaultPresetAddress?: string, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(attrs) ||
            !ValidateModule.checkId(layerMetaId)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/" + layerMetaId + "/feature?" +
            "importDataType=" + (importDataType ? importDataType : "JSON") +
            ( selector ? "&selector=" + selector : "") +
            ( defaultPresetAddress ? "&defaultPresetAddress=" + es.util.url.stringify(defaultPresetAddress) : "");

        if (importDataType == "XML" || importDataType == "CSV") {
            opts = $.extend(opts, {"doNotJsonify": true});
        }
        
        return this.rqBroker.doPost({
            url: url,
            opts: opts,
            data: attrs
        });
    }
    
    /**
     * Edit a feature by passing key-val pairs for attributes and a layerMetaId.
     * Feature will be geocoded on the server using same geomExtractor as the original dataset if any of the fields used for geocoding in the original upload have changed.
     * 
     * @param attrs feature attributes as key-value pairs e.g. {"ADDRESS": "6801 Lake Worth Rd", "CITY": "Lake Worth", "COUNTY": "Palm Beach", "STATE": "Florida", "ZIP": "33467"}
     * @param layerMetaId
     * @param criteria the attribute that should be used as an identifier. e.g. can be {"es_id": 123} i.e. eSpatial's internal id, if know. Or, any other uniquely identifying attribute e.g. {"company": "eSpatial"}, {"email" : "a@b.c"}
     * @param defaultPresetAddress used for geocoding, defaults to United States
     * @param opts
     */
    importEdit(attrs, layerMetaId: number, criteria, defaultPresetAddress, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(attrs) ||
            !ValidateModule.checkId(layerMetaId)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/" + layerMetaId + "/feature?" +
            ( criteria ? "&criteria=" + es.util.url.stringify(criteria) : "") +
            ( defaultPresetAddress ? "&defaultPresetAddress=" + es.util.url.stringify(defaultPresetAddress) : "");

        return this.rqBroker.doPut({
            url: url,
            opts: opts,
            data: attrs
        });
    }

    /**
     * Get the sample file.
     * 
     * @param geoMapId
     * @param fileName
     * @param opts
     */
    getSampleFile(geoMapId: number, fileName?: string, opts?: ApiOpts) {
        let url = es.api.getURL(this.resourceName) + "/get_sample_file?geoMapId=" + geoMapId;
        if (fileName !== undefined) {
            url = url + "&fileName=" + fileName;
        }

        return this.rqBroker.doPost({
            url: url,
            opts: opts
        });
    }

    /**
     * Get the progress of an import.
     * 
     * @param taskId
     * @param opts 
     * 
     * @returns 
     */
    getProgress(taskId: number, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(taskId)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/get_progress?taskId=" + taskId;
        return this.rqBroker.doGet({
            url: url,
            opts: opts
        }).done((rs) => {
            rs.taskId = taskId;
            
            this.progressDone(rs);
        });
    }

    /**
     * Stop the progress of an import.
     * @param taskId
     * @param saveProgress (optional)
     */
    stopProgress(taskId: number, saveProgress?: boolean, opts?: ApiOpts) {
        this.cache.clear('AsyncTasks');
        
        let url = es.api.getURL(this.resourceName) + "/get_progress?taskId=" + taskId + "&stopProcess=true";
        if (saveProgress !== undefined) {
            url = url + "&saveProgress=" + saveProgress;
        }

        return this.rqBroker.doGet({
            url: url,
            opts: opts
        }).done((rs) => {
            rs.taskId = taskId;
            
            this.progressDone(rs);
        });
    }
    
    /**
     * 
     * @param rs 
     * 
     * @returns 
     */
    private progressDone(rs) {
        this.cache.clear('AsyncTasks');
        
        if (!rs.statusObj || (rs.statusObj.status >= 0 && rs.statusObj.status < 5)) {
            return;
        }
        
        if (rs.statusObj.layerMeta) {
            this.cache?.clear("LayerMeta_GeoMap");
            this.cache?.clear("Security", "ownership_layermeta");
            // TODO FIXME Should we also do cache.clear("LayerMeta"); ??
            
            this.cache?.clear("Thematic_GET_COLOR");
            this.cache?.clear("Thematic_GET_SYMBOL");
            // no need to cache.clear("Thematic_GET_SIZE") as we only recalculate distinct thematics and size is always range thematic
            
            if (rs.statusObj.status == 5) {
                this.eventChannel?.fireEvent("onCreateLayerMeta", rs.statusObj.layerMeta);
            }
        }
        
        const layerView = rs.statusObj.layerView;
        if (!layerView) {
            return;
        }
        
        this.cache.clear("GeoMap");
        this.cache.clear("LayerView");
        this.cache.clear("LayerView_GeoMap");
        this.cache.clear("Security", "ownership_layerview");
        
        rs.statusObj.layerView.attributeNames.forEach((attrName) => {
            const cacheId = rs.statusObj.layerView.id + '_' + attrName;

            this.cache.clear("Thematic_GET_COLOR", cacheId);
            this.cache.clear("Thematic_GET_SYMBOL", cacheId);
            // no need to cache.clear("Thematic_GET_SIZE", cacheId) as we only recalculate distinct thematics and size is always range thematic
        });
        
        Promise.all([
            es.api.layerInfo.getForLayerView(layerView.id),
            es.api.geoMap.get(layerView.geoMapId, {"noCache": true})
        ]).then(([layerInfo, geoMap]) => {
            this.eventChannel?.fireEvent("onCreateLayerView", layerView, layerInfo, geoMap.lastModified);
        });
    }

    /**
     * Get valid attribute name from an attribute title
     * 
     * @param attributeName 
     * @param opts 
     * 
     * @returns 
     */
    getValidAttributeName(attributeName: string, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/get_valid_attribute_name/" + attributeName;
        return this.rqBroker.doGet({url: url, opts: opts});
    }
}
